import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "@/core/auth/useSession";
import { useTenant } from "@/core/tenant/TenantProvider";
import { Button, Dialog } from "@/core/ui";
import { useNotificationPreference, useUpdateNotificationPreference } from "./api";

const SNOOZE_KEY = "bt-notifications-prompt-snoozed";

function isSnoozed(): boolean {
  try {
    return sessionStorage.getItem(SNOOZE_KEY) === "1";
  } catch {
    return false;
  }
}

function snooze(): void {
  try {
    sessionStorage.setItem(SNOOZE_KEY, "1");
  } catch {
    // Private mode or blocked storage: the prompt simply comes back on the next load.
  }
}

/**
 * First-login prompt (ADR 0021). Asks once whether the user wants e-mail; both answers
 * are recorded, so it never returns after an answer. Closing it without answering only
 * snoozes it for this browser session.
 */
export function NotificationOptInDialog() {
  const { t } = useTranslation();
  const tenant = useTenant();
  const session = useSession();
  const signedIn = Boolean(session.data?.user);
  const preference = useNotificationPreference(signedIn && tenant.notifications_enabled);
  const update = useUpdateNotificationPreference();
  const [snoozed, setSnoozed] = useState(isSnoozed);
  const answered = useRef(false);

  const open = signedIn && !snoozed && preference.data?.decided === false;

  const answer = (optIn: boolean) => {
    update.mutate(
      { on_publication: optIn, weekly_digest: optIn, on_duel_turn: false },
      { onSuccess: () => (answered.current = true) },
    );
  };
  const close = () => {
    // The native close event also fires when the answer lands and `open` flips to false.
    if (answered.current) return;
    snooze();
    setSnoozed(true);
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title={t("account.notifications.prompt_title")}
      footer={
        <>
          <Button variant="outline" onClick={() => answer(false)} disabled={update.isPending}>
            {t("account.notifications.prompt_decline")}
          </Button>
          <Button onClick={() => answer(true)} loading={update.isPending}>
            {t("account.notifications.prompt_accept")}
          </Button>
        </>
      }
    >
      <p className="bt-notify-prompt__body">{t("account.notifications.prompt_body")}</p>
    </Dialog>
  );
}
